import { useState, useContext, useEffect, useRef } from "react";
import SessionContext from "SessionContext";
import { Link, withRouter } from "react-router-dom";
import { Button, Card, CardHeader, Label } from 'reactstrap';
import { EditDescription } from "../partials/edit-name-description";
import ConfirmSuccess from "../core/ui/confirm-success";
import InfoPopover from "../core/ui/info-popover";

const UploadDetails = (props) => {
  const context = useContext(SessionContext);

  const [upload, setUpload] = useState(undefined);
  const [prevUrl, setPrevUrl] = useState("uploads");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [failedToLoad, setFailedToLoad] = useState(false);
  const pollIDRef = useRef(null);

  useEffect(() => {
    if(props.subpage) {
      getUpload();
      setPrevUrl("uploads");
    } else {
      const refer =
        (props.location && props.location.state && props.location.state.refer)
          ? props.location.state.refer
          : "uploads";
      setPrevUrl(refer);
    }
    return () => {
      clearTimeout(pollIDRef.current);
    }
    // eslint-disable-next-line
  },[]);

  function getUpload() {
    const upload_id = props.subpage;
    context
      .query("uploads/get", { id: upload_id })
      .then((res) => {
        setUpload(res);
        setFailedToLoad(false);
        if(res && res.status === "PROCESSING") {
          pollForUpdatedStatus(res.id, res.status);
        }
      })
      .catch((err) => {
        setFailedToLoad(true);
        console.log("Failed to load data", err)
      })
  }

  function pollForUpdatedStatus(uploadId, lastStatus) {
    context
      .query("uploads/status", { id: uploadId })
      .then((response) => {
        if (response.status === "PROCESSING") {
          pollIDRef.current = setTimeout(() => pollForUpdatedStatus(uploadId, response.status), 1000);
        } else {
          if (lastStatus !== response.status) {
            // Get the upload if the status changes so we can display the results.
            getUpload();
          }
        }
      });
  }

  function toggleEditNameModal() {
    setShowEditModal((prev) => !prev);
  }

  function onAcceptChanges(_description) {
    let updatedDescription = {...upload, description: _description};
    context.command("upload/rename",
      updatedDescription, {}, { defaultErrorHandling: false }
    )
    .then((res) => {
      setShowEditModal(false);
      setShowSuccess(true);
      setTimeout(() => {
        props.history.push("/uploads");
      }, 2000);
    })
    .catch((error) => {
      console.log("Failed to update the upload file", error)
    })
  }


  let upload_report = (upload && upload.upload_report) || {};

  if(failedToLoad) {
    return (
      <div className="page upload-details-page">
        <div className="list-header sticky-page-header">
          <Link to={`/${prevUrl}`}>
            <Button type="button" className="new-button">
              <i className="bi bi-chevron-left" />{" "} 
              {prevUrl === "location-uploads" ? "Location Uploads" : prevUrl}
            </Button>
          </Link>
          <h1>Upload Details</h1>
          <i className="spacer" />
        </div>
        <div className="m-5">
          <h5>Details could not be found for that upload.</h5>
        </div>
      </div>
    )
  }

  if (!upload) {
    return <span className="loading">Loading Upload</span>;
  }
    
  const access = context.appState.userCan;

  return (
    <div className="page upload-details-page">
      <div className="list-header sticky-page-header">
        <Link to={`/${prevUrl}`}>
          <Button type="button" className="new-button">
            <i className="bi bi-chevron-left" />{" "} 
            {prevUrl === "location-uploads" ? "Location Uploads" : prevUrl}
          </Button>
        </Link>
        <h1>Upload Details</h1>
        <i className="spacer" />
      </div>

      <section className="upload-details mt-4">
        <Card className="preview">
          <CardHeader>
            <h2>Filename:
              <span className="upload-details-info">
                {upload.filename}
              </span>
            </h2>
          </CardHeader>
          <div className="labeled-items horizontal m-3">
            <div className="labeled-item primary description-row">
              <Label>Description: 
                {access('upload_edit') && <>
                  <i className="bi bi-pencil" title="Edit Description" onClick={toggleEditNameModal}/>
                </>}
              </Label>
              <span className="value">{upload.description}</span>
            </div>
          </div>
          <div className="labeled-items horizontal m-3 second-row">
            <div className="labeled-item primary">
              <Label>Uploaded BY:</Label>
              <span className="value">{upload.uploaded_by}</span>
            </div>
            <div className="labeled-item primary">
              <Label>Date/Time:</Label>
              <span className="value">{upload.ingress_date}</span>
            </div>
            <div className="labeled-item primary">
              <Label>Status:</Label>
              <span className="value">{upload.status}</span>
            </div>
          </div>

          {(upload.status === "Complete" && Object.keys(upload_report).length) ? (
            <><div className="labeled-items horizontal m-3">
              <table className="minimal-table">
                <thead>
                  <tr>
                    <th className="description-header">Description</th>
                    <th className="count-header text-end">Records Remaining
                      <span className="remaining-info-icon">
                        <InfoPopover 
                        title="Records Remaining" 
                        message="Count of records remaining after the corresponding hygiene step is applied." 
                      />
                      </span>
                    </th>
                    <th className="percent-header text-end pe-3 border-end">% of File</th>
                    <th className="count-header text-end">Records Dropped
                      <span className="remaining-info-icon">
                        <InfoPopover 
                        title="Records Dropped" 
                        message="Count of records removed via the corresponding hygiene step." 
                      />
                      </span>
                    </th>
                    <th className="percent-header text-end">% of File</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="value-header">Total Records Uploaded
                      <span className="upload-details-info-icon">
                        <InfoPopover 
                        title="Total Records" 
                        message="Total records in the upload file." 
                      />
                      </span>
                    </td>
                    <td className="text-end">{upload_report.total_records_uploaded_ct}</td>
                    <td className="text-end pe-3 border-end">{upload_report.total_records_uploaded_pct}</td>
                    <td className="text-end pe-2">&ndash;</td>
                    <td className="text-end">0%</td>
                  </tr>
                  <tr>
                    <td className="value-header">Name and Address Populated
                      <span className="upload-details-info-icon">
                        <InfoPopover 
                        title="Name and Address Populated" 
                        message="Only records with name and address fields fully populated are eligible for matching." 
                      />
                      </span>
                    </td>
                    <td className="text-end">{upload_report.name_address_populated_ct}</td>
                    <td className="text-end pe-3 border-end">{upload_report.name_address_populated_pct}</td>
                    <td className="text-end">{upload_report.name_address_not_populated_ct}</td>
                    <td className="text-end">{upload_report.name_address_not_populated_pct}</td>
                  </tr>
                  <tr>
                    <td className="value-header">Duplicates and Bad Addresses Removed
                      <span className="upload-details-info-icon">
                        <InfoPopover 
                        title="Duplicates Removed" 
                        message="Records with the same First Name, Last Name, and Address are dropped, as well as invalid addresses." 
                      />
                      </span>
                    </td>
                    <td className="text-end">{upload_report.duplicates_remaining_ct}</td>
                    <td className="text-end pe-3 border-end">{upload_report.duplicates_remaining_pct}</td>
                    <td className="text-end">{upload_report.duplicates_removed_ct}</td>
                    <td className="text-end">{upload_report.duplicates_removed_pct}</td>
                  </tr>                    
                  <tr>
                    <td className="value-header">Global Suppressions Removed
                      <span className="upload-details-info-icon">
                        <InfoPopover 
                        title="Global Suppressions Removed" 
                        message="Uploads are scrubbed against global suppression lists such as deceased, prisons, do not contact, etc." 
                      />
                      </span>
                    </td>
                    <td className="text-end">{upload_report.global_suppress_remaining_ct}</td>
                    <td className="text-end pe-3 border-end">{upload_report.global_suppress_remaining_pct}</td>
                    <td className="text-end">{upload_report.global_suppress_removed_ct}</td>
                    <td className="text-end">{upload_report.global_suppress_removed_pct}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="labeled-items horizontal m-3">
              <table className="minimal-table">
                <thead>
                  <tr>
                    <th className="description-header">Description</th>
                    <th className="count-header matched text-end">Number of Records</th>
                    <th className="percent-header not-matched text-end">% of Records After Hygiene</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="value-header">Matched to AudienceMaker Demographics
                      <span className="upload-details-info-icon">
                        <InfoPopover 
                        title="Matched to AudienceMaker Demographics" 
                        message="Records that match to AudienceMaker’s individual level demographics and are eligible for modeling." 
                      />
                      </span>
                    </td>
                    <td className="text-end">{upload_report.am_demographic_remaining_ct}</td>
                    <td className="text-end">{upload_report.am_demographic_remaining_pct}</td>
                  </tr>
                  <tr>
                    <td className="value-header">Not Matched to AudienceMaker Demographics</td>
                    <td className="text-end">{upload_report.am_demographic_removed_ct}</td>
                    <td className="text-end">{upload_report.am_demographic_removed_pct}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            </>
          ) : ""}
        </Card>

      </section>
      <EditDescription 
        toggleShowModal={toggleEditNameModal}
        data={{name: upload.filename, description: upload.description || ""}}
        confirmChanges={onAcceptChanges}
        showModal={showEditModal}
      />
      <ConfirmSuccess show={showSuccess} label="Upload updated successfully." />
    </div>
  );
};
UploadDetails.site_section = "uploads";
export default withRouter(UploadDetails);
